/**
 * CREATIVE DETONATOR - Auth State Management
 * Phase 2: Store Facade Implementation
 *
 * Centralized authentication state manager.
 * Handles user sessions, profile data, and credits.
 *
 * MIGRATION: This module now syncs with the central Store.
 * AuthState class manages auth flow while Store holds the state.
 *
 * FRONTEND-25: Uses Logger for environment-aware logging
 */

import { supabase, getCurrentUser, getSessionToken, isAuthConfigured } from './client.js';
import { API_BASE } from '../../../js/api/endpoints.js';
import { createLogger } from '../../../js/core/logger.js';
import {
  initActions as initAuthActions,
  setUser as actionSetUser,
  setProfile as actionSetProfile,
  setCredits as actionSetCredits,
  deductCredits as actionDeductCredits,
  addCredits as actionAddCredits,
  setLoading as actionSetLoading,
  setError as actionSetError,
  clearAuth as actionClearAuth
} from '../../../js/store/actions/auth.js';

// FRONTEND-25: Namespaced logger for auth module
const log = createLogger('auth');

// Note: API_BASE is now imported from endpoints.js (Single Source of Truth)

// ============================================================================
// STORE ACCESS HELPER
// ============================================================================

function getStore() {
  return window.Store;
}

// ============================================================================
// AUTH STATE CLASS
// Phase 2: Syncs with Store for reactive updates
// ============================================================================

class AuthState {
  constructor() {
    // Local state (for compatibility during initialization)
    this._user = null;
    this._profile = null;
    this._loading = true;
    this._initialized = false;
    this._listeners = [];
    this._storeInitialized = false;
  }

  /**
   * Initialize auth state
   * Call this once on app startup
   */
  async init() {
    if (this._initialized) {
      return this;
    }

    this.loading = true;

    // Initialize Store actions if Store is available
    if (getStore() && !this._storeInitialized) {
      initAuthActions();
      this._storeInitialized = true;
    }

    // If auth is not configured, mark as initialized but skip auth setup
    if (!isAuthConfigured()) {
      this.loading = false;
      this._initialized = true;
      log.info('Auth not configured, skipping initialization');
      return this;
    }

    try {
      // Get initial user
      this._user = await getCurrentUser();

      if (this._user) {
        await this.fetchProfile();
      }

      // Sync to Store
      this._syncToStore();
    } catch (error) {
      log.error('Init error:', error.message);
      this._setError(error.message);
    }

    this.loading = false;
    this._initialized = true;

    // Listen for auth state changes (only if supabase is available)
    if (supabase) {
      supabase.auth.onAuthStateChange(async (event, session) => {
        log.debug('Auth event:', event);

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          this._user = session?.user || null;
          if (this._user) {
            await this.fetchProfile();
          }
        } else if (event === 'SIGNED_OUT') {
          this._user = null;
          this._profile = null;
        }

        this._syncToStore();
        this.notifyListeners();
      });
    }

    return this;
  }

  /**
   * Fetch user profile from backend
   */
  async fetchProfile() {
    if (!this._user) return;

    try {
      const token = await getSessionToken();
      const response = await fetch(`${API_BASE}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        this._profile = data.profile;
        // FRONTEND-25: Log profile loaded status without sensitive data
        log.debug('Profile loaded successfully');
        this._syncToStore();
      } else if (response.status === 401) {
        // Token expired or invalid
        this._user = null;
        this._profile = null;
        this._syncToStore();
      }
    } catch (error) {
      log.error('Failed to fetch profile:', error.message);
    }
  }

  /**
   * Refresh profile (after credit change, etc.)
   */
  async refreshProfile() {
    await this.fetchProfile();
    this.notifyListeners();
  }

  /**
   * Subscribe to state changes
   * @param {Function} callback - Called with (user, profile) on changes
   * @returns {Function} - Unsubscribe function
   */
  onStateChange(callback) {
    this._listeners.push(callback);
    return () => {
      this._listeners = this._listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners of state change
   */
  notifyListeners() {
    this._listeners.forEach(cb => {
      try {
        cb(this._user, this._profile);
      } catch (error) {
        log.error('Listener error:', error.message);
      }
    });
  }

  // ============================================================================
  // STORE SYNCHRONIZATION (Phase 2)
  // ============================================================================

  /**
   * Sync local state to Store
   * @private
   */
  _syncToStore() {
    const store = getStore();
    if (!store) return;

    // Use actions if initialized, otherwise direct set
    if (this._storeInitialized) {
      // Set user (this also sets isAuthenticated)
      actionSetUser(this._user);
      actionSetProfile(this._profile);
      actionSetLoading(this._loading);
    } else {
      store.set('auth.user', this._user);
      store.set('auth.profile', this._profile);
      store.set('auth.isAuthenticated', !!this._user);
      store.set('auth.loading', this._loading);
    }
  }

  /**
   * Set error state
   * @private
   */
  _setError(error) {
    const store = getStore();
    if (store && this._storeInitialized) {
      actionSetError(error);
    } else if (store) {
      store.set('auth.error', error);
    }
  }

  // ============================================================================
  // GETTERS / SETTERS (Phase 2: Read from Store when available)
  // ============================================================================

  get user() {
    // Try Store first, fall back to local
    return getStore()?.get('auth.user') ?? this._user;
  }
  set user(value) {
    this._user = value;
    this._syncToStore();
  }

  get profile() {
    return getStore()?.get('auth.profile') ?? this._profile;
  }
  set profile(value) {
    this._profile = value;
    this._syncToStore();
  }

  get loading() {
    return getStore()?.get('auth.loading') ?? this._loading;
  }
  set loading(value) {
    this._loading = value;
    if (getStore() && this._storeInitialized) {
      actionSetLoading(value);
    } else if (getStore()) {
      getStore().set('auth.loading', value);
    }
  }

  get initialized() {
    return this._initialized;
  }

  get listeners() {
    return this._listeners;
  }

  /**
   * Check if user is logged in
   */
  get isLoggedIn() {
    return getStore()?.get('auth.isAuthenticated') ?? !!this._user;
  }

  /**
   * Get current credits
   */
  get credits() {
    const profile = this.profile;
    return profile?.credits || 0;
  }

  /**
   * Check if user is admin
   */
  get isAdmin() {
    const profile = this.profile;
    return profile?.role === 'admin';
  }

  /**
   * Get user's display name
   */
  get displayName() {
    const profile = this.profile;
    const user = this.user;
    return profile?.display_name || user?.email?.split('@')[0] || 'User';
  }

  /**
   * Get user's email
   */
  get email() {
    const user = this.user;
    return user?.email || '';
  }

  /**
   * Get user ID
   */
  get userId() {
    const user = this.user;
    return user?.id || null;
  }

  // ============================================================================
  // CREDIT OPERATIONS (Phase 2: Use Store actions)
  // ============================================================================

  /**
   * Update credits in Store
   * @param {number} credits - New credit amount
   */
  updateCredits(credits) {
    if (this._profile) {
      this._profile.credits = credits;
    }
    if (getStore() && this._storeInitialized) {
      actionSetCredits(credits);
    }
    this.notifyListeners();
  }

  /**
   * Deduct credits
   * @param {number} amount - Amount to deduct
   */
  deductCredits(amount) {
    if (getStore() && this._storeInitialized) {
      actionDeductCredits(amount);
    } else {
      const currentCredits = this.credits;
      this.updateCredits(Math.max(0, currentCredits - amount));
    }
    this.notifyListeners();
  }

  /**
   * Add credits
   * @param {number} amount - Amount to add
   */
  addCredits(amount) {
    if (getStore() && this._storeInitialized) {
      actionAddCredits(amount);
    } else {
      const currentCredits = this.credits;
      this.updateCredits(currentCredits + amount);
    }
    this.notifyListeners();
  }

  // ============================================================================
  // LOGOUT
  // ============================================================================

  /**
   * Clear auth state (logout)
   */
  clear() {
    this._user = null;
    this._profile = null;
    this._loading = false;

    if (getStore() && this._storeInitialized) {
      actionClearAuth();
    } else if (getStore()) {
      getStore().set('auth', {
        user: null,
        profile: null,
        isAuthenticated: false,
        loading: false,
        error: null
      });
    }

    this.notifyListeners();
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const authState = new AuthState();

// Expose to window for debugging and non-module scripts
window.authState = authState;

// ============================================================================
// STORE SYNCHRONIZATION EXPORTS (FRONTEND-30)
// Kept for backwards compatibility
// ============================================================================

/**
 * Sync AuthState to Store
 * @deprecated Use authState methods directly, syncs automatically
 */
export function syncAuthToStore() {
  authState._syncToStore();
}

/**
 * Initialize Store connection for auth
 * @deprecated Called automatically by authState.init()
 */
export function initAuthStoreSync() {
  if (!getStore()) {
    log.warn('Store not available for sync');
    return;
  }

  // Initialize actions if not already done
  if (!authState._storeInitialized) {
    initAuthActions();
    authState._storeInitialized = true;
  }

  // Initial sync
  authState._syncToStore();

  // Subscribe to auth changes and sync to Store
  authState.onStateChange((user, profile) => {
    authState._syncToStore();
  });

  log.debug('Auth connected to Store with action modules');
}

// Log migration status in development
if (import.meta.env?.DEV) {
  console.info('[AuthState] Phase 2: Now syncs with central Store.');
}
